import ShoppingBasketRoundedIcon from '@mui/icons-material/ShoppingBasketRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useBasketSettings, useUpdateBasketSettings } from '../../hooks';
import { toSettingsUpdate, useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import type { CartMergeStrategy } from '../../model/settingsTypes';
import {
  FeedbackSnackbar,
  NumberField,
  SelectField,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
} from '../components';
import { CART_MERGE_STRATEGY_OPTIONS } from '../options';

/** Basket settings: cart lifetime, limits and guest-merge behaviour. */
export function BasketSettingsPage() {
  const { t } = useTranslation('settings');
  const { canView, canManage } = useDomainPermissions('basket');
  const query = useBasketSettings();
  const update = useUpdateBasketSettings();
  const form = useSettingsForm(query.data);
  const { feedback, success, error: notifyError, clear } = useFeedback();

  const handleSave = () => {
    if (!form.draft) {
      return;
    }
    update.mutate(toSettingsUpdate(form.draft), {
      onSuccess: () => success(t('basketSettings.savedFeedback')),
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title={t('basketSettings.title')}
      subtitle={t('basketSettings.subtitle')}
      icon={<ShoppingBasketRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={form.dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={form.reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;
        return (
          <Stack spacing={2.5}>
            <SettingsCard title={t('basketSettings.cartBehaviourSection.title')} icon={<ShoppingBasketRoundedIcon />}>
              <SwitchField
                label={t('basketSettings.cartBehaviourSection.guestCartLabel')}
                description={t('basketSettings.cartBehaviourSection.guestCartDescription')}
                checked={draft.guestCartEnabled}
                onChange={(value) => form.setField('guestCartEnabled', value)}
                disabled={disabled}
              />
              <SelectField
                label={t('basketSettings.cartBehaviourSection.mergeStrategyLabel')}
                description={t('basketSettings.cartBehaviourSection.mergeStrategyDescription')}
                value={draft.mergeStrategyOnLogin}
                options={CART_MERGE_STRATEGY_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))}
                onChange={(value) =>
                  form.setField('mergeStrategyOnLogin', value as CartMergeStrategy)
                }
                disabled={disabled}
              />
              <SwitchField
                label={t('basketSettings.cartBehaviourSection.reserveStockLabel')}
                description={t('basketSettings.cartBehaviourSection.reserveStockDescription')}
                checked={draft.reserveStockOnAdd}
                onChange={(value) => form.setField('reserveStockOnAdd', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('basketSettings.cartBehaviourSection.saveForLaterLabel')}
                checked={draft.saveForLaterEnabled}
                onChange={(value) => form.setField('saveForLaterEnabled', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard title={t('basketSettings.limitsSection.title')} icon={<TuneRoundedIcon />}>
              <NumberField
                label={t('basketSettings.limitsSection.cartLifetimeLabel')}
                description={t('basketSettings.limitsSection.cartLifetimeDescription')}
                value={draft.cartTtlMinutes}
                onChange={(value) => form.setField('cartTtlMinutes', value)}
                disabled={disabled}
                min={0}
                suffix={t('basketSettings.limitsSection.cartLifetimeSuffix')}
              />
              <NumberField
                label={t('basketSettings.limitsSection.maxLineItemsLabel')}
                value={draft.maxLineItems}
                onChange={(value) => form.setField('maxLineItems', value)}
                disabled={disabled}
                min={1}
              />
              <NumberField
                label={t('basketSettings.limitsSection.maxQuantityLabel')}
                value={draft.maxQuantityPerItem}
                onChange={(value) => form.setField('maxQuantityPerItem', value)}
                disabled={disabled}
                min={1}
              />
              <NumberField
                label={t('basketSettings.limitsSection.minOrderAmountLabel')}
                value={draft.minOrderAmount}
                onChange={(value) => form.setField('minOrderAmount', value)}
                disabled={disabled}
                min={0}
              />
            </SettingsCard>
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
